import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { Key, RefObject } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';
import { computeScrollMargin, getPrecedingElements } from './scrollMargin';

/** A collapsed CardRow: min-height 4.5rem + 2px card border. Real heights come from measureElement. */
const DEFAULT_ESTIMATED_SIZE_PX = 74;
/** 0.5rem — the usual gap between list cards. */
const DEFAULT_GAP_PX = 8;
const DEFAULT_OVERSCAN = 6;

export interface UseVirtualListParams<T> {
    items: readonly T[];
    /** Stable key of an item. Keep the function itself stable (module-level / useCallback): its identity resets the measurement cache. */
    getItemKey: (item: T, index: number) => Key;
    /**
     * The element that scrolls (overflow-y: auto), passed as an element — keep it in state via a callback ref
     * (`ref={setScrollEl}`), not `ref.current`: the list's layout effects run before an ancestor's ref attaches.
     * It must have `scroll-behavior: auto` (index.css sets smooth on '*'): the virtualizer corrects scrollTop when
     * a row is measured, and a smooth write turns each correction into an animation that cuts the scroll short.
     */
    scrollElement: HTMLElement | null;
    /** Starting row height before measurement, px. */
    estimateSize?: number;
    /** Space between rows, px — the only way to space rows (see VirtualList). */
    gap?: number;
    /** Rows rendered beyond each edge of the view. */
    overscan?: number;
    /** A change scrolls the container to its very top (content above the list included); the first render does not scroll. */
    scrollResetKey?: Key | null;
}

export interface UseVirtualListReturn {
    listRef: RefObject<HTMLDivElement | null>;
    virtualItems: VirtualItem[];
    totalSize: number;
    scrollMargin: number;
    measureElement: (node: Element | null) => void;
}

/**
 * Virtualizes a vertical list rendered inside `scrollElement`.
 *
 * The list's offset from the top of the scroll content (scrollMargin) follows size changes of what is laid out
 * above it. Keep dynamic content above the list inside a persistent wrapper: a sibling inserted before the list
 * after mount is not observed, so the visible range would drift by its height.
 */
export function useVirtualList<T>({
    items,
    getItemKey,
    scrollElement,
    estimateSize = DEFAULT_ESTIMATED_SIZE_PX,
    gap = DEFAULT_GAP_PX,
    overscan = DEFAULT_OVERSCAN,
    scrollResetKey,
}: UseVirtualListParams<T>): UseVirtualListReturn {
    const listRef = useRef<HTMLDivElement | null>(null);
    const [scrollMargin, setScrollMargin] = useState(0);

    useLayoutEffect(() => {
        const list = listRef.current;
        if (!list || !scrollElement) return;
        const measure = () => {
            setScrollMargin(
                computeScrollMargin({
                    targetTop: list.getBoundingClientRect().top,
                    rootTop: scrollElement.getBoundingClientRect().top,
                    rootClientTop: scrollElement.clientTop,
                    rootScrollTop: scrollElement.scrollTop,
                }),
            );
        };
        measure();
        const observer = new ResizeObserver(measure);
        getPrecedingElements(list, scrollElement).forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [scrollElement]);

    useEffect(() => {
        if (
            import.meta.env.DEV &&
            scrollElement &&
            window.getComputedStyle(scrollElement).scrollBehavior === 'smooth'
        ) {
            console.warn(
                '[VirtualList] the scroll element has scroll-behavior: smooth — set it to auto, or scrolling will jump.',
                scrollElement,
            );
        }
    }, [scrollElement]);

    const getVirtualKey = useCallback(
        (index: number) => getItemKey(items[index], index),
        [items, getItemKey],
    );

    const virtualizer = useVirtualizer({
        count: items.length,
        getScrollElement: () => scrollElement,
        estimateSize: () => estimateSize,
        getItemKey: getVirtualKey,
        gap,
        overscan,
        scrollMargin,
    });

    const prevResetKeyRef = useRef(scrollResetKey);
    useLayoutEffect(() => {
        if (Object.is(prevResetKeyRef.current, scrollResetKey)) return;
        prevResetKeyRef.current = scrollResetKey;
        virtualizer.scrollToOffset(0);
    }, [scrollResetKey, virtualizer]);

    return {
        listRef,
        virtualItems: virtualizer.getVirtualItems(),
        totalSize: virtualizer.getTotalSize(),
        scrollMargin,
        measureElement: virtualizer.measureElement,
    };
}
